/**
 * ODIV Inbox v2 — bootstrap
 *
 * Architecture:
 *   stores/      → reactive state (single source of truth)
 *   components/  → Preact UI components bound to stores
 *   services/    → API clients, realtime subscriptions, push
 *
 * Boot flow:
 *   1. Try to consume ?magic=<token> from URL (dashboard handoff)
 *   2. Try to rehydrate an existing session via /refresh — the in-memory
 *      JWT is null on every fresh page load (post #108: JWT is no longer
 *      persisted to localStorage), but the HttpOnly `inbox_jwt` cookie
 *      survives across loads. /refresh with cookie fallback returns a
 *      fresh access token + session data if the cookie is still valid.
 *   3. If /refresh 401s → show Login.
 *   4. After login: register service worker → init FCM push → connect Realtime.
 */

import { render }   from 'preact';
import { html }     from 'htm/preact';
import { useStore } from '@nanostores/preact';

import { $screen, navigate, openThread, showToast, setPendingShare } from './stores/ui.js';
import { $jwt, $session, setSession, getAccountId } from './stores/session.js';
import { $chats, $accountUnread, $serverAccountUnread } from './stores/chats.js';
import { SCREENS } from './shared/constants.js';
import * as api from './services/api.js';
import { connectRealtime, disconnectRealtime, reconnectRealtime } from './services/realtime.js';
import { initPush, initPushPermissionWatch, maybeAutoPromptOnFirstInstall } from './services/push.js';
import { initPwaInstall }      from './services/pwa-install.js';
import { initOnlineStatus }    from './services/online-status.js';
import { initKeyboardAware }   from './services/keyboard-aware.js';
import { navigateAfterLogin } from './services/permissions-check.js';
import { setHydrateRetryHandler } from './services/permission-degraded.js';
import { hydrateChannelMeta } from './services/channel-colors.js';
import { hydrateTeam }        from './services/team-directory.js';
import { hydrateQuickReplies } from './services/quick-replies.js';
import { loadChats }          from './services/chats-service.js';

import { ChatList }              from './components/ChatList.js';
import { Thread }                from './components/Thread.js';
import { Login }                 from './components/Login.js';
import { Profile }               from './components/Profile.js';
import { Settings }              from './components/Settings.js';
import { QuickReplies }          from './components/QuickReplies.js';
import { PermissionErrorScreen } from './components/PermissionErrorScreen.js';
import { Toaster }               from './components/Toaster.js';
import { OfflineBanner }         from './components/OfflineBanner.js';


// Register the hydrate-retry callback so permission-degraded.js can re-run
// the four background services when the page becomes visible again or when
// the user dismisses the degraded banner. permission-degraded.js can't
// import these directly without creating a circular dependency.
setHydrateRetryHandler(() => {
  hydrateChannelMeta();
  hydrateTeam();
  hydrateQuickReplies();
  // loadChats throws on 403 (caller-driven); also re-run so a sticky
  // empty list recovers without the user navigating away.
  loadChats({ filter: 'all' }).catch(() => {});
});

// ── Service worker registration ──
//
// S4 / Q4=A — single reload authority. The SW's `activate` handler
// (inbox/sw.js) is the sole path that reloads open tabs after a deploy.
// It calls clients.claim() and then client.navigate() on every window
// client, which reliably swaps the tab onto the new bundle exactly once.
//
// Previous multi-path behaviour caused the "stuck loader on first
// open after update" bug: controllerchange reload + 'sw-activated'
// postMessage reload + checkStaleBuild forceReloadForUpdate could all
// fire for the same transition, racing each other mid-reload and
// sometimes landing the page half-booted.
//
// This registrar keeps:
//   - reg.update() on visibility regain (debounced to 5 min so a chatty
//     user doesn't hammer a sw.js HEAD every tab switch)
//   - updatefound → skipWaiting so the new SW activates without waiting
//     for the old one to be retired naturally
//   - the SW → page postMessage channel, but only for the quick-action
//     messages (Reply / Mark done) the notification banner routes back
//     to the live tab. The reload-on-sw-activated branch has been
//     removed — the SW force-navigates clients directly.
async function registerSW() {
  if (!('serviceWorker' in navigator)) {
    console.warn('[v2 boot] service workers not supported');
    return null;
  }

  navigator.serviceWorker.addEventListener('message', (e) => {
    if (e.data?.type === 'inbox-quick-reply') _handleQuickReplyMessage(e.data);
    if (e.data?.type === 'inbox-quick-done')  _handleQuickDoneMessage(e.data);
    // T15 — SW forwards push badge counts here when at least one client
    // is alive, so the main thread owns the OS badge exclusively. The
    // SW falls back to setAppBadge itself only when all clients are
    // closed. Writing to $serverAccountUnread re-evaluates the derived
    // $accountUnread (max with local $chats sum) and fires the OS
    // badge listener — no duplicate setAppBadge calls between threads.
    if (e.data?.type === 'badge-sync' && typeof e.data.count === 'number') {
      $serverAccountUnread.set(Math.max(0, Math.floor(e.data.count)));
    }
  });

  try {
    // updateViaCache:'none' — bypass the HTTP cache when checking sw.js
    // updates. Without this, Chrome only re-checks sw.js every 24h.
    const reg = await navigator.serviceWorker.register('/inbox/sw.js', {
      scope: '/inbox/',
      updateViaCache: 'none',
    });
    if (!reg) {
      console.warn('[v2 boot] SW register resolved without a registration (likely blocked by environment)');
      return null;
    }

    // Force an immediate update check on every page load.
    reg.update().catch(e => console.warn('[v2 boot] reg.update() failed:', e.message));

    // If a new SW is waiting from a prior visit, activate it now.
    if (reg.waiting) {
      reg.waiting.postMessage({ type: 'skip-waiting' });
    }

    // Watch for future updates — promote 'installed' → 'active' ASAP.
    reg.addEventListener('updatefound', () => {
      const nw = reg.installing;
      if (!nw) return;
      nw.addEventListener('statechange', () => {
        if (nw.state === 'installed' && navigator.serviceWorker.controller) {
          nw.postMessage({ type: 'skip-waiting' });
        }
      });
    });

    // Debounced update-check on visibility regain. Was unthrottled before,
    // so a user tab-switching 20× an hour hit the server 20× — now at
    // most once per 5 min. Plenty fast to catch deploys during the day.
    const VIS_UPDATE_MIN_INTERVAL_MS = 5 * 60 * 1000;
    let _lastVisUpdateAt = 0;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - _lastVisUpdateAt < VIS_UPDATE_MIN_INTERVAL_MS) return;
      _lastVisUpdateAt = now;
      reg.update().catch(() => {});
    });

    return reg;
  } catch (e) {
    console.error('[v2 boot] SW registration failed:', e.message);
    return null;
  }
}

// ── Post-login setup (idempotent) ────────────────────────────────────────────

async function postLoginSetup(accountId) {
  if (!accountId) return;

  // Connect Supabase Realtime
  try {
    await connectRealtime(accountId);
  } catch (e) {
    console.error('[v2 boot] realtime connect failed:', e.message);
  }

  // Initialize FCM push (non-blocking — ok if user denies permission)
  initPush().catch(e => console.error('[v2 boot] push init failed:', e.message));

  // #196 — One-shot first-install auto-prompt. Helper internally checks
  // standalone/permission/localStorage gates and no-ops unless this is
  // a fresh install in standalone mode with permission still 'default'.
  // 3s delay so the agent sees the chat list first, not a permission
  // modal slammed onto the boot screen.
  //
  // S13: gate on document.visibilityState so we don't slam a permission
  // prompt the instant the user tab-switches away during boot. Also
  // cancellable — the Timeout returns nothing meaningful but a Safari
  // cold-launch that's killed by the OS before 3s won't race with a
  // second login's prompt firing because setTimeout callbacks on
  // destroyed pages just don't execute.
  setTimeout(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    maybeAutoPromptOnFirstInstall().catch(e =>
      console.warn('[v2 boot] first-install prompt failed:', e?.message));
  }, 3000);
}

// ── Quick-action handlers from SW notification buttons (#181) ────────────────
//
// The SW posts these messages when the agent taps a Reply or Mark-done
// action on an FCM notification banner. data.chatId is the FCM payload's
// chat_id field — for message.incoming pushes this is the customer's
// PHONE (E.164), set by inbox-webhook.js fcmFanOut. We resolve it back
// to a chat row via $chats so we can hand sendMedia/sendMessage the
// proper phone + channel pair.

async function _handleQuickReplyMessage({ chatId, channelPhoneId, text }) {
  if (!chatId || !text || !text.trim()) return;
  const row = _findChatRowByPhoneOrContactId(chatId, channelPhoneId);
  if (!row) {
    console.warn('[v2 quick-reply] no chat row for', chatId, '— opening picker');
    showToast('Open the chat first to reply', 2400);
    return;
  }
  try {
    // Use the same dedicated text-send path the Composer uses so optimistic
    // UI, dedup, channel routing, and 24h-window guard all run the same way.
    // S16: pin $openChatChannel to the resolved row BEFORE sending so
    // resolveActiveChannel picks the right channel (instead of the stale
    // last-viewed thread's channel).
    const { $openChatChannel } = await import('./stores/ui.js');
    $openChatChannel.set(row.channelPhoneId || null);
    const { sendText } = await import('./services/messages-outbound.js');
    await sendText(row.phone, text.trim());
    showToast(`Reply sent to ${row.name || row.phone}`, 2200);
  } catch (e) {
    console.error('[v2 quick-reply] send failed:', e?.message);
    showToast(`Quick reply failed: ${e?.message || 'unknown error'}`, 3500);
  }
}

async function _handleQuickDoneMessage({ chatId, channelPhoneId }) {
  if (!chatId) return;
  const row = _findChatRowByPhoneOrContactId(chatId, channelPhoneId);
  if (!row) {
    console.warn('[v2 quick-done] no chat row for', chatId);
    return;
  }
  try {
    await api.markDone(row.contactId, row.phone, row.channelPhoneId);
    showToast(`Marked done — ${row.name || row.phone}`, 2200);
  } catch (e) {
    console.error('[v2 quick-done] failed:', e?.message);
    showToast(`Mark done failed: ${e?.message || 'unknown error'}`, 3500);
  }
}

// S16 — matches contact by chatId (contactId OR phone), preferring the
// row whose channelPhoneId matches the hint. Falls back to first match
// by contactId/phone when no hint (legacy notifications).
function _findChatRowByPhoneOrContactId(id, channelHint = null) {
  const all = $chats.get();
  let fallback = null;
  for (const k in all) {
    const c = all[k];
    if (!c) continue;
    const idMatches = c.phone === id || c.contactId === id;
    if (!idMatches) continue;
    if (channelHint && c.channelPhoneId === channelHint) return c;
    if (!fallback) fallback = c;
  }
  return channelHint ? fallback : fallback; // fallback in both cases — but prefer exact match above
}

// ── Notification deep-link (#174) ────────────────────────────────────────────
//
// FCM webpush.fcm_options.link sends the user to
//   /inbox/?chat=<phoneOrContactId>&ch=<channelPhoneId>
// when they tap a notification. `chat` identifies the conversation
// (may be a CRM contact ObjectId OR a phone number — FCM payloads
// historically use phone); `ch` is the per-channel phone_number_id
// so multi-channel contacts resolve to the EXACT row the notification
// came from.
//
// S16: `ch` is new. Prior to S16 this function matched on contactId
// alone and picked the first $chats row with a matching contactId —
// wrong for multi-channel contacts (two rows share contactId). When
// the first-matched row differed from the notification's true source,
// the user landed on the wrong channel. With `ch`, the lookup is
// deterministic; `chat` alone still works (with first-match fallback)
// for legacy notifications in flight during the rollout.

function _consumePendingChatDeepLink(chatTarget, channelHint) {
  if (!chatTarget) return;
  // Strip the params so a refresh doesn't re-deep-link into the same chat.
  try {
    const url = new URL(location.href);
    url.searchParams.delete('chat');
    url.searchParams.delete('ch');
    history.replaceState(null, '', url.pathname + (url.search || ''));
  } catch {}

  const tryOpen = (chats) => {
    let fallbackMatch = null;
    for (const key in chats) {
      const c = chats[key];
      if (!c) continue;
      // Match by contactId OR phone — FCM payloads vary. Both identify
      // the conversation; we need the per-channel row to know which
      // thread to open.
      const matchesTarget =
        c.contactId === chatTarget || c.phone === chatTarget;
      if (!matchesTarget) continue;

      // Exact channel match → open this row, stop searching.
      if (channelHint && c.channelPhoneId === channelHint) {
        openThread({
          chatKey:        c.id,
          chatId:         c.contactId,
          phone:          c.phone,
          channelPhoneId: c.channelPhoneId,
        });
        return true;
      }

      // Remember the first target match as a fallback for legacy
      // notifications that don't carry a channel hint.
      if (!fallbackMatch) fallbackMatch = c;
    }

    if (fallbackMatch && !channelHint) {
      openThread({
        chatKey:        fallbackMatch.id,
        chatId:         fallbackMatch.contactId,
        phone:          fallbackMatch.phone,
        channelPhoneId: fallbackMatch.channelPhoneId,
      });
      return true;
    }
    return false;
  };

  if (tryOpen($chats.get())) return;

  let unsub = null;
  const giveUp = setTimeout(() => {
    if (unsub) unsub();
    console.warn('[v2 boot] deep-link gave up — chat not found:', chatTarget);
    showToast('Couldn\'t find that chat — it may have been reassigned', 3000);
  }, 8000);

  unsub = $chats.listen((chats) => {
    if (tryOpen(chats)) {
      clearTimeout(giveUp);
      if (unsub) unsub();
    }
  });
}

// ── Boot flow ────────────────────────────────────────────────────────────────

async function init() {

  // Register service workers in parallel with auth check
  registerSW();

  // #188 / #189 / #187 — these listeners must attach BEFORE first paint
  // so we don't miss beforeinstallprompt (Chrome fires it once, very
  // early), the first online/offline event, or a permission flip that
  // happens while the user is still on the boot screen. All three are
  // idempotent and cheap.
  initPwaInstall();
  initOnlineStatus();
  initPushPermissionWatch();
  // Keyboard-aware shell sizing — pins .inbox-screen to the visual
  // viewport so the header stays put and the composer floats just
  // above the on-screen keyboard. Must run before first paint of the
  // thread screen so the CSS variables are populated when the layout
  // measures itself.
  initKeyboardAware();

  // #174 — Capture ?chat=<contactId> BEFORE we strip it on history.replaceState.
  // FCM webpush.fcm_options.link routes notification clicks to /inbox/?chat=<id>;
  // the SW also opens this URL when no tab is in focus. We consume it once the
  // chat list has loaded (see _consumePendingChatDeepLink).
  const params = new URLSearchParams(location.search);
  const pendingChatDeepLink = params.get('chat') || null;
  // S16 — optional per-channel hint so multi-channel contacts deep-link
  // to the EXACT row the notification came from.
  const pendingChannelHint  = params.get('ch')   || null;

  // #193 — Web Share Target. The manifest registers /inbox/ as a GET share
  // target with params {sh_title, sh_text, sh_url}. Stash whatever the OS
  // sent into $pendingShare; ChatList renders the picker banner. Same
  // history.replaceState below strips the params so a refresh doesn't
  // re-trigger the share state.
  if (params.has('sh_title') || params.has('sh_text') || params.has('sh_url')) {
    setPendingShare({
      title: params.get('sh_title') || '',
      text:  params.get('sh_text')  || '',
      url:   params.get('sh_url')   || '',
    });
    // Strip the share params so a refresh doesn't re-inject.
    try {
      const url = new URL(location.href);
      url.searchParams.delete('sh_title');
      url.searchParams.delete('sh_text');
      url.searchParams.delete('sh_url');
      history.replaceState(null, '', url.pathname + (url.search || ''));
    } catch {}
  }

  // #181 — Quick-reply notification action fallback. When the SW couldn't
  // postMessage to a live client (no tab was open), it openWindow()s
  // /inbox/?chat=<id>&reply=<text> and we honor it here by stuffing the
  // text into $pendingShare so Composer pre-fills on chat open. Same
  // pattern as Web Share Target — single seam.
  const pendingReplyText = params.get('reply') || null;
  const pendingDoneFlag  = params.get('done')  === '1';
  if (pendingReplyText) {
    setPendingShare({ text: pendingReplyText, title: '', url: '' });
    try {
      const url = new URL(location.href);
      url.searchParams.delete('reply');
      history.replaceState(null, '', url.pathname + (url.search || ''));
    } catch {}
  }
  if (pendingDoneFlag) {
    try {
      const url = new URL(location.href);
      url.searchParams.delete('done');
      history.replaceState(null, '', url.pathname + (url.search || ''));
    } catch {}
    // Defer to after navigateAfterLogin + the chat list lands. Same atom
    // listener model the chat deep-link uses.
    if (pendingChatDeepLink) {
      const fireDone = (chats) => {
        for (const k in chats) {
          if (chats[k]?.contactId === pendingChatDeepLink || chats[k]?.phone === pendingChatDeepLink) {
            _handleQuickDoneMessage({ chatId: pendingChatDeepLink });
            return true;
          }
        }
        return false;
      };
      if (!fireDone($chats.get())) {
        const unsub = $chats.listen((c) => { if (fireDone(c)) unsub(); });
        setTimeout(() => { try { unsub && unsub(); } catch {} }, 8000);
      }
    }
  }

  // 1. Magic link from dashboard handoff?
  // NOTE: postLoginSetup is NOT called explicitly here — setSession() fires
  // the $session listener below which calls postLoginSetup. Calling both
  // would create parallel connectRealtime invocations that tear each other
  // down mid-handshake (the realtime cascade we hit on 2026-04-13).
  const magicToken = params.get('magic');
  if (magicToken) {
    try {
      const data = await api.verifyMagicLink(magicToken);
      if (data?.accessToken) {
        setSession(data.accessToken, data.session);
        history.replaceState(null, '', '/inbox/');
        // #165: audit CRM role permissions before dropping into chat list.
        await navigateAfterLogin();
        if (pendingChatDeepLink) _consumePendingChatDeepLink(pendingChatDeepLink, pendingChannelHint);
        return;
      }
    } catch (e) {
      console.warn('[v2 boot] magic link failed:', e.message);
    }
  }

  // 2. Rehydrate from HttpOnly cookie via /refresh.
  //
  // Post #108 the JWT is not persisted to localStorage, so $jwt is null
  // on every fresh page load. /refresh accepts a cookie fallback and
  // returns a fresh accessToken in the response body — we stuff that
  // into $jwt (in-memory only) and repopulate the session. If the
  // cookie is missing or expired, /refresh 401s and we fall through to
  // the login screen.
  //
  // Session data (accountId, email, channels, permissions, etc) is
  // fetched via /me as a second step so we have the full shape the
  // components expect. We could skip /me if /refresh returned the full
  // session envelope, but for now it doesn't — keeping it as a
  // two-step boot keeps the server changes minimal and backwards-
  // compatible with existing clients that still send Authorization.
  try {
    const refreshed = await api.refreshSession();
    if (refreshed?.accessToken) {
      // Put the fresh JWT in memory BEFORE the follow-up /me so
      // _authHeaders() picks it up.
      $jwt.set(refreshed.accessToken);
      const me = await api.fetchMe();
      setSession(refreshed.accessToken, me);
      // #165: audit CRM role permissions before dropping into chat list.
      await navigateAfterLogin();
      if (pendingChatDeepLink) _consumePendingChatDeepLink(pendingChatDeepLink, pendingChannelHint);
      return;
    }
    console.warn('[v2 boot] /refresh returned no accessToken, falling through');
  } catch (e) {
    // 401 is the expected path for "no valid cookie" — just log and
    // drop to login. Other errors also drop to login; they'll be
    // visible in console for debugging.
  }

  // 3. No session → login
  navigate(SCREENS.LOGIN);
}

// Connect realtime + push when login completes (session populates after Login)
$session.listen((session) => {
  if (session?.accountId) {
    postLoginSetup(session.accountId);
  } else {
    disconnectRealtime();
  }
});

// Reconnect realtime + refresh data on tab refocus. Two jobs:
//   1. reconnectRealtime() — re-establish the WebSocket if it died
//   2. loadChats() — catch up on messages that arrived while backgrounded
//      (broadcasts during the gap were lost even if WS recovers)
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && getAccountId()) {
    reconnectRealtime();
    loadChats({}).catch(() => {});
  }
});

// S2/S3 — App badge count on the dock / home-screen icon.
//
// Single source of truth: $accountUnread, populated from the /chats
// response envelope (server RPC inbox_account_unread_badge). The number
// respects 24h decay + per-user scope (agent sees only assigned-to-them;
// owner sees assigned-to-them + unassigned).
//
// We deliberately do NOT sum chats[k].unread here — the CRM /chats
// endpoint paginates and some unread chats may not be in the first
// page, so summing locally drifts from the server's view. Reading a
// single server-computed number makes the OS badge match exactly what
// an incoming FCM push sets.
//
// Reset on session change so a logout → login as a different user
// doesn't inherit the previous user's badge value.
if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
  // T12 — drive the OS badge purely from $accountUnread, no short-circuit.
  //
  // Pre-T12 we had `if (n === _lastBadgeTotal) return;` as a perf
  // optimization. That was the bug: the service worker's FCM
  // onBackgroundMessage ALSO calls setAppBadge(N) from a different
  // thread, so the OS badge can diverge from $accountUnread without
  // $accountUnread changing value. Example:
  //   1. Thread open → mark-read → $accountUnread 0 → clearAppBadge ✓
  //   2. FCM push → SW.setAppBadge(1) (runs in SW, bypasses main-thread listener)
  //   3. Client realtime handler skips unread bump (open thread), so
  //      $accountUnread stays at 0 — the listener never re-fires
  //   4. Badge stuck at 1 forever until the user manually reopens a
  //      thread whose unread IS tracked in $accountUnread.
  // Always calling setAppBadge/clearAppBadge makes the main-thread
  // value authoritative and heals any SW divergence on every update.
  // The API is cheap; repeated identical calls are free.
  const _syncBadge = (total) => {
    const n = Math.max(0, Math.floor(Number(total) || 0));
    if (n > 0) navigator.setAppBadge(n).catch(() => {});
    else      navigator.clearAppBadge().catch(() => {});
  };
  $accountUnread.listen(_syncBadge);

  // T12 — visibilitychange sync. When the PWA returns to foreground,
  // the SW may have set a stale badge while we were away. Re-assert
  // our authoritative value. Cheap single call, no network hop.
  //
  // Also force a /chats refetch (debounced to 5s) — this pulls the
  // server-authoritative account_unread via the RPC, covering the
  // case where the user returns to the chat list (not a thread) and
  // the SW has a stale count. loadChats updates $accountUnread from
  // its meta envelope, so the listener above re-fires with the
  // correct number and syncs the OS badge.
  if (typeof document !== 'undefined') {
    let _lastVisFetchAt = 0;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      _syncBadge($accountUnread.get());
      const now = Date.now();
      if (now - _lastVisFetchAt > 5000) {
        _lastVisFetchAt = now;
        loadChats({}).catch(() => {});
      }
    });
  }
}

// ── Root component ───────────────────────────────────────────────────────────

function App() {
  return html`
    <${OfflineBanner} />
    <${Screen} />
    <${Toaster} />
  `;
}

function Screen() {
  const screen = useStore($screen);

  if (screen === SCREENS.INIT) {
    return html`
      <div class="inbox-screen inbox-screen-init" style="display:flex">
        <div class="inbox-init-inner">
          <img src="/assets/images/logo-512.png" alt="ODIV" width="56" height="56" class="inbox-init-logo" />
          <div class="inbox-spinner"></div>
        </div>
      </div>
    `;
  }

  if (screen === SCREENS.LOGIN)            return html`<${Login} />`;
  if (screen === SCREENS.CHAT_LIST)        return html`<${ChatList} />`;
  if (screen === SCREENS.THREAD)           return html`<${Thread} />`;
  if (screen === SCREENS.PROFILE)          return html`<${Profile} />`;
  if (screen === SCREENS.SETTINGS)         return html`<${Settings} />`;
  if (screen === SCREENS.QUICK_REPLIES)    return html`<${QuickReplies} />`;
  if (screen === SCREENS.PERMISSION_ERROR) return html`<${PermissionErrorScreen} />`;

  console.error('[v2] Unknown screen:', screen);
  return html`<div>Unknown screen: ${screen}</div>`;
}

// ── Bootstrap ────────────────────────────────────────────────────────────────

render(html`<${App} />`, document.getElementById('app'));
init();

